"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { Bell, X } from "lucide-react";

function applicationServerKey(value) {
  const padding = "=".repeat((4 - value.length % 4) % 4);
  const raw = atob((value + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map(character => character.charCodeAt(0)));
}

export default function MobileNotificationControl({ latestArticles = [], compact = false, routePrefix = null }) {
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [message, setMessage] = useState("");
  const closeRef = useRef(null);

  useEffect(() => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) return;
    navigator.serviceWorker.register("/sw.js").then(async (registration) => {
      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) return;
      const query = new URLSearchParams({ endpoint: subscription.endpoint, ...(routePrefix ? { routePrefix } : {}) });
      const response = await fetch(`/api/push/subscription?${query}`);
      const state = response.ok ? await response.json() : { enabled: false };
      setEnabled(state.enabled === true);
      if (state.enabled !== true && state.unsubscribeBrowser === true) await subscription.unsubscribe();
    }).catch(() => setMessage("Push notifications could not be initialized on this device."));
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    const closeOnEscape = (event) => event.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  async function toggleNotifications() {
    if (enabled) {
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          const response = await fetch(`/api/push/subscription${routePrefix ? `?routePrefix=${encodeURIComponent(routePrefix)}` : ""}`, { method: "DELETE", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ endpoint: subscription.endpoint }) });
          if (!response.ok) throw new Error();
          const result = await response.json();
          if (result.unsubscribeBrowser) await subscription.unsubscribe();
        }
        setEnabled(false);
        setMessage("Push notifications are off on this device. Latest headlines remain available below.");
      } catch { setMessage("Notifications could not be turned off. Please try again."); }
      return;
    }
    if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) {
      setMessage("This browser does not support Web Push notifications. Latest headlines remain available below.");
      return;
    }
    let keyData;
    try {
      const keyResponse = await fetch(`/api/push/vapid-public-key${routePrefix ? `?routePrefix=${encodeURIComponent(routePrefix)}` : ""}`);
      keyData = await keyResponse.json();
      if (!keyResponse.ok) throw new Error(keyData.error);
    } catch (error) {
      setMessage(error.message || "Push notifications are not available for this publication.");
      return;
    }
    const permission = window.Notification.permission === "default"
      ? await window.Notification.requestPermission()
      : window.Notification.permission;
    if (permission !== "granted") {
      setMessage(permission === "denied" ? "Notifications are blocked in your browser settings." : "Notification permission was not granted.");
      return;
    }
    try {
      const registration = await navigator.serviceWorker.register("/sw.js");
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: applicationServerKey(keyData.publicKey) });
      const response = await fetch(`/api/push/subscription${routePrefix ? `?routePrefix=${encodeURIComponent(routePrefix)}` : ""}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(subscription) });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);
      setEnabled(true);
      setMessage("Push notifications are enabled for this publication on this device.");
    } catch (error) {
      setEnabled(false);
      setMessage(error.message || "Push notifications could not be enabled. Please try again.");
    }
  }

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} aria-label="Open notifications" aria-expanded={open} className="inline-flex items-center justify-center p-2 text-black hover:text-gray-600">
        <Bell size={compact ? 20 : 22} strokeWidth={1.5} />
      </button>
      {open && (
        <>
          <button type="button" aria-label="Close notifications" className="fixed inset-0 z-[103] bg-black/40" onClick={() => setOpen(false)} />
          <section role="dialog" aria-modal="true" aria-labelledby="notification-title" className="fixed inset-y-0 right-0 z-[104] flex w-[min(100%,390px)] flex-col overflow-hidden bg-white text-black shadow-2xl">
            <header className="flex items-center justify-between border-b border-gray-300 px-5 py-4">
              <h2 id="notification-title" className="font-classic text-2xl font-bold">Notifications</h2>
              <button ref={closeRef} type="button" onClick={() => setOpen(false)} aria-label="Close notifications" className="p-2"><X size={24} /></button>
            </header>
            <div className="border-b border-gray-300 p-5">
              <div className="flex items-center justify-between gap-4">
                <div><p className="font-bold">Push notifications</p><p className="mt-1 text-sm text-gray-600">Receive selected alerts from this publication on this device.</p></div>
                <button type="button" role="switch" aria-checked={enabled} onClick={toggleNotifications} className={`min-w-16 rounded-full px-3 py-2 text-xs font-black uppercase ${enabled ? "bg-black text-white" : "bg-gray-200 text-black"}`}>{enabled ? "On" : "Off"}</button>
              </div>
              {message && <p role="status" className="mt-3 text-sm text-gray-600">{message}</p>}
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-5">
              <h3 className="mb-1 text-xs font-black uppercase tracking-[0.18em]">Latest articles</h3>
              <p className="mb-3 text-sm text-gray-600">The latest headlines are always available here, independent of push-notification status.</p>
              {latestArticles.length ? (
                <ol className="divide-y divide-gray-200 border-y border-black">
                  {latestArticles.slice(0, 10).map((article) => (
                    <li key={article.id}><Link href={article.url} onClick={() => setOpen(false)} className="block py-4 font-classic text-lg font-bold leading-tight hover:underline">{article.title}</Link></li>
                  ))}
                </ol>
              ) : <p className="py-6 text-sm text-gray-600">No recent articles are available.</p>}
            </div>
          </section>
        </>
      )}
    </>
  );
}
